import { useContext } from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Grid,
  Typography,
} from "@mui/material";
import EmojiEventsRoundedIcon from "@mui/icons-material/EmojiEventsRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import SwapHorizRoundedIcon from "@mui/icons-material/SwapHorizRounded";
import AccountCircleRoundedIcon from "@mui/icons-material/AccountCircleRounded";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import PageLayout from "../../Components/UI/PageLayout.js";
import { LoginContext } from "../../Components/Login/LoginContext";
import { siteUpdates } from "../../Data/siteUpdates.js";
import logo from "../../transparent-logo.png";

const formatDate = (dateStr) =>
  new Date(dateStr).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

const HomePage = () => {
  const [auth] = useContext(LoginContext);
  const latestUpdate = siteUpdates[0];

  const quickLinks = [
    {
      label: "Competitions",
      description: "Browse all competitions",
      to: "/competitions",
      icon: <EmojiEventsRoundedIcon sx={{ fontSize: 40 }} />,
    },
    {
      label: "Teams",
      description: "View teams and players",
      to: "/teams",
      icon: <GroupsRoundedIcon sx={{ fontSize: 40 }} />,
    },
    {
      label: "Transfer Market",
      description: "Browse transfer requests",
      to: "/transferRequests",
      icon: <SwapHorizRoundedIcon sx={{ fontSize: 40 }} />,
    },
    ...(auth.loggedIn
      ? [
          {
            label: "Dashboard",
            description: "Your competitions and teams",
            to: "/dashboard",
            icon: <DashboardRoundedIcon sx={{ fontSize: 40 }} />,
          },
          {
            label: "Profile",
            description: "Manage your account",
            to: "/profile",
            icon: <AccountCircleRoundedIcon sx={{ fontSize: 40 }} />,
          },
        ]
      : []),
  ];

  return (
    <PageLayout>
      {/* Hero */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          pt: 4,
          pb: 5,
        }}
      >
        <Box
          component="img"
          src={logo}
          alt="PCFA Logo"
          sx={{
            maxWidth: { xs: "220px", sm: "150px" },
            width: "100%",
            filter: "drop-shadow(0px 4px 10px rgba(110, 200, 45, 0.3))",
          }}
        />
        <Box sx={{ textAlign: { xs: "center", sm: "left" } }}>
          <Typography
            variant="h4"
            component="h1"
            sx={{ color: "primary.main", fontWeight: 600, mb: 1 }}
          >
            Pro Clubs Football Association
          </Typography>
          <Typography variant="body1" color="text.secondary">
            The Premier Virtual Football League
          </Typography>
        </Box>
      </Box>

      {/* Quick Links */}
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
        Quick Links
      </Typography>
      <Grid container spacing={2} justifyContent="center" sx={{ mb: 5 }}>
        {quickLinks.map((link) => (
          <Grid item xs={12} sm={6} md={2} key={link.to}>
            <Card sx={{ height: "100%", backgroundColor: "#2a3447" }}>
              <CardActionArea
                component={Link}
                to={link.to}
                sx={{ height: "100%" }}
              >
                <CardContent sx={{ textAlign: "center", py: 3 }}>
                  <Box sx={{ color: "primary.main", mb: 1 }}>{link.icon}</Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {link.label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {link.description}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Latest Update teaser */}
      {latestUpdate && (
        <Box sx={{ mb: 5 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Latest Update
            </Typography>
            <Typography
              component={Link}
              to="/updates"
              variant="body2"
              sx={{
                color: "primary.main",
                textDecoration: "none",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              See all updates →
            </Typography>
          </Box>
          <Card sx={{ backgroundColor: "#2a3447" }}>
            <CardActionArea component={Link} to="/updates">
              <CardContent sx={{ p: 3 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 0.5, display: "block" }}
                >
                  {formatDate(latestUpdate.date)}
                </Typography>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, mb: 1 }}
                >
                  {latestUpdate.title}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    overflow: "hidden",
                    display: "-webkit-box",
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: "vertical",
                  }}
                >
                  {latestUpdate.body}
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        </Box>
      )}
    </PageLayout>
  );
};

export default HomePage;
